import { memo, useEffect, useRef, useState } from "react";
import { features } from "../../data/mockData";
import { Link } from "react-router-dom";
import { floatingItems } from "./floatingItems";
import "./style.css";
import ananas from "../../assets/images/fruits/ananas.webp";
import logo from "../../assets/images/logo0.webp";
import smoke3 from "../../assets/images/smoke3.webp";
import smoke from "../../assets/images/smoke.webp";

const HeroSection = memo(() => {
  const pineappleRef = useRef(null);
  const [showText, setShowText] = useState(false);
  const heroRef = useRef(null);
  const pineappleImgRef = useRef(null);


const textRef = useRef(null);

useEffect(() => {
  const el  = pineappleRef.current;
  const img = pineappleImgRef.current || (el && el.querySelector('img.fruit-center'));
  const txt = textRef.current;
  if (!el || !img) return;

  // ---- helpers & params (must be in this scope) ----
  const clamp = (v, min = 0, max = 1) => Math.max(min, Math.min(v, max));
  const lerp  = (a, b, t) => a + (b - a) * t;
  const ROT_PER_PX = 0.12;   // ° per px
  const PARALLAX   = 0.18;   // vh share for translateY

  let fade = 0;       // for IO opacity (optional)
  let ticking = false; // <-- define here to avoid no-undef

  // IntersectionObserver — controls pineapple opacity
  const thresholds = Array.from({ length: 21 }, (_, i) => i / 20);
  const io = new IntersectionObserver(
    (entries) => {
      const e = entries[0];
      const r = e.intersectionRatio;
      const eased = r <= 0 ? 0 : r >= 1 ? 1 : r * r * (3 - 2 * r);
      fade = clamp(eased, 0, 1);
      img.style.opacity = fade.toFixed(3);
    },
    { root: null, rootMargin: '0px', threshold: thresholds }
  );
  io.observe(el);

  const render = () => {
    const vh = document.documentElement.clientHeight || window.innerHeight;

    // --- pineapple parallax/rotate ---
    const rect = el.getBoundingClientRect();
    const angle = window.scrollY * ROT_PER_PX;
    const yParallax = lerp(0, vh * PARALLAX, (vh - rect.top) / (vh + (rect.height || vh)));
    img.style.transform = `translate3d(0, ${yParallax.toFixed(1)}px, 0) rotate(${angle.toFixed(2)}deg)`;

    // --- text appear & scale ---
    if (txt) {
      const r = txt.getBoundingClientRect();
      const start = vh * 0.8;                              // when top hits 80% vh
      const end   = Math.max(start - (r.height || 1), vh * 0.25); // finish near center
      let t = (start - r.top) / (start - end);
      t = clamp(t, 0, 1);
      const ease = t * t * (3 - 2 * t);
      const scale = lerp(0.85, 1.12, ease);
      const opacity = ease;

      txt.style.setProperty('--tScale', scale.toFixed(3));
      txt.style.setProperty('--tOpacity', opacity.toFixed(3));
    }

    ticking = false;
  };

  const onScrollOrResize = () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(render);
    }
  };

  // init
  onScrollOrResize();
  const onLoad = () => onScrollOrResize();
  img.addEventListener('load', onLoad, { once: true });

  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize);

  return () => {
    io.disconnect();
    window.removeEventListener('scroll', onScrollOrResize);
    window.removeEventListener('resize', onScrollOrResize);
  };
}, []);


// ====== FLOATING FRUITS: bounce + pulse + z-index swap (desktop only) ======
  useEffect(() => {
    const container = heroRef.current;
    if (!container) return;

    const fruitsEls = Array.from(container.querySelectorAll('.fruit'));
    if (!fruitsEls.length) return;

    // media-query: до 1024px не двигаем
    const mq = window.matchMedia('(max-width: 1024px)');
    let disabled = mq.matches;

    const toPx = (val, cw, ch, axis = 'x') => {
      if (typeof val !== 'string') return Number(val) || 0;
      const n = parseFloat(val);
      if (val.endsWith('vw')) return (n / 100) * cw;
      if (val.endsWith('vh')) return (n / 100) * ch;
      if (val.endsWith('%'))  return (n / 100) * (axis === 'x' ? cw : ch);
      return n; // px
    };
    const box = () => {
      const r = container.getBoundingClientRect();
      return { w: r.width, h: r.height };
    };

    // ——— Статика для mobile/tablet (≤1024) ———
    const placeStatic = () => {
      const { w: CW, h: CH } = box();
      fruitsEls.forEach(el => {
        const img = el._img || el.querySelector('img');
        if (!img) return;

        // width (из data-size с поддержкой vmin/vw/vh/%)
        const sizeStr = el.dataset.size || '';
        let EW;
        if (sizeStr) {
          const n = parseFloat(sizeStr);
          if (sizeStr.endsWith('vmin'))      EW = (Math.min(CW, CH) / 100) * n;
          else if (sizeStr.endsWith('vw'))   EW = (CW / 100) * n;
          else if (sizeStr.endsWith('vh'))   EW = (CH / 100) * n;
          else if (sizeStr.endsWith('%'))    EW = (CW / 100) * n;
          else if (sizeStr.endsWith('px'))   EW = n;
          else                               EW = n || Math.max(48, Math.min(160, CW * 0.08));
        } else {
          const r0 = el.getBoundingClientRect();
          EW = r0.width || Math.max(48, Math.min(160, CW * 0.08));
        }
        const iw = img.naturalWidth  || 100;
        const ih = img.naturalHeight || 100;
        const EH = EW * (ih / iw);

        const sTop  = el.dataset.startTop  || '50%';
        const sLeft = el.dataset.startLeft || '50%';
        let x0 = toPx(sLeft, CW, CH, 'x') - EW / 2;
        let y0 = toPx(sTop,  CW, CH, 'y') - EH / 2;
        x0 = Math.max(0, Math.min(CW - EW, x0));
        y0 = Math.max(0, Math.min(CH - EH, y0));

        el.style.position  = 'absolute';
        el.style.left = '0px';
        el.style.top  = '0px';
        el.style.willChange = 'auto'; // не просим GPU в статике
        el.style.width  = `${EW}px`;
        el.style.height = `${EH}px`;
        el.style.transform = `translate3d(${x0}px, ${y0}px, 0) scale(1)`;
        // базовый слой — под лого (можно читать data-z-weight, но в статике это излишне)
        el.style.zIndex = '1';
      });
    };

    // ——— Движок для desktop ———
    const S = []; // {el,x,y,vx,vy,w,h,scaleBase,scaleAmp,omega,phase,z,swapAt}
    let raf = 0;
    let last = performance.now();
    const baseSpeed = 28; // px/s

    const initDynamic = () => {
      const { w: CW, h: CH } = box();

      fruitsEls.forEach(el => {
        el.style.animation = 'none';
        el.style.position  = 'absolute';
        el.style.left = '0px';
        el.style.top  = '0px';
        el.style.willChange = 'transform';

        const img = el._img || el.querySelector('img');
        if (!img) return;

        // width/height
        const sizeStr = el.dataset.size || '';
        let EW;
        if (sizeStr) {
          const n = parseFloat(sizeStr);
          if (sizeStr.endsWith('vmin'))      EW = (Math.min(CW, CH) / 100) * n;
          else if (sizeStr.endsWith('vw'))   EW = (CW / 100) * n;
          else if (sizeStr.endsWith('vh'))   EW = (CH / 100) * n;
          else if (sizeStr.endsWith('%'))    EW = (CW / 100) * n;
          else if (sizeStr.endsWith('px'))   EW = n;
          else                               EW = n || Math.max(48, Math.min(160, CW * 0.08));
        } else {
          const r0 = el.getBoundingClientRect();
          EW = r0.width || Math.max(48, Math.min(160, CW * 0.08));
        }
        const iw = img.naturalWidth  || 100;
        const ih = img.naturalHeight || 100;
        const EH = EW * (ih / iw);

        const sTop  = el.dataset.startTop  || '50%';
        const sLeft = el.dataset.startLeft || '50%';
        let x0 = toPx(sLeft, CW, CH, 'x') - EW / 2;
        let y0 = toPx(sTop,  CW, CH, 'y') - EH / 2;
        x0 = Math.max(0, Math.min(CW - EW, x0));
        y0 = Math.max(0, Math.min(CH - EH, y0));

        // направление
        let dx = toPx(el.dataset.tx || '0', CW, CH, 'x');
        let dy = toPx(el.dataset.ty || '0', CW, CH, 'y');
        if (dx === 0 && dy === 0) { dx = Math.random()*2-1; dy = Math.random()*2-1; }
        const len = Math.hypot(dx, dy) || 1;
        dx /= len; dy /= len;

        const speed = baseSpeed * (0.7 + Math.random()*0.6);

        // pulse (диапазоны из data-* или дефолты)
        const ampRange = (el.dataset.pulseAmp || '').split(',').map(v=>parseFloat(v)).filter(v=>!isNaN(v));
        const perRange = (el.dataset.pulsePeriod || '').split(',').map(v=>parseFloat(v)).filter(v=>!isNaN(v));
        const ampMin = ampRange[0] ?? 0.05, ampMax = ampRange[1] ?? 0.10;
        const perMin = perRange[0] ?? 2.8,  perMax = perRange[1] ?? 6.3;
        const scaleBase = 0.92 + Math.random()*0.20;
        const scaleAmp  = ampMin + Math.random() * Math.max(0, ampMax - ampMin);
        const periodSec = perMin + Math.random() * Math.max(0, perMax - perMin);
        const omega     = (2*Math.PI) / periodSec;
        const phase     = Math.random()*Math.PI*2;

        // z-index c весом
        const zWeight = parseFloat(el.dataset.zWeight ?? '');
        const underProb = isNaN(zWeight) ? 0.5 : Math.min(1, Math.max(0, zWeight));
        const z = Math.random() < underProb ? 1 : 3;
        el.style.zIndex = String(z);
        const swapAt = performance.now() + (3000 + Math.random()*5000);

        el.style.width  = `${EW}px`;
        el.style.height = `${EH}px`;
        el.style.transform = `translate3d(${x0}px, ${y0}px, 0)`;

        S.push({ el, x: x0, y: y0, vx: dx*speed, vy: dy*speed, w: EW, h: EH,
          scaleBase, scaleAmp, omega, phase, z, swapAt });
      });
    };

    const tick = (tNow) => {
      if (disabled) return; // на всякий случай (если переключили MQ в рантайме)
      const dt = Math.min(0.05, (tNow - last) / 1000);
      last = tNow;

      const { w: W, h: H } = box();

      for (const s of S) {
        const scale = s.scaleBase + s.scaleAmp * Math.sin(s.phase + tNow/1000 * s.omega);
        const effW = s.w * scale;
        const effH = s.h * scale;

        let nx = s.x + s.vx * dt;
        let ny = s.y + s.vy * dt;

        if (nx < 0) { nx = 0; s.vx = Math.abs(s.vx); }
        else if (nx + effW > W) { nx = W - effW; s.vx = -Math.abs(s.vx); }

        if (ny < 0) { ny = 0; s.vy = Math.abs(s.vy); }
        else if (ny + effH > H) { ny = H - effH; s.vy = -Math.abs(s.vy); }

        s.x = nx; s.y = ny;

        if (tNow >= s.swapAt) {
          s.z = s.z === 3 ? 1 : 3;
          s.el.style.zIndex = String(s.z);
          s.swapAt = tNow + (3000 + Math.random()*5000);
        }

        s.el.style.transform =
            `translate3d(${nx.toFixed(1)}px, ${ny.toFixed(1)}px, 0) scale(${scale.toFixed(3)})`;
      }

      raf = requestAnimationFrame(tick);
    };

    // ——— старт/стоп в зависимости от ширины ———
    const start = () => {
      if (disabled) {
        // статика
        placeStatic();
        // и на resize просто перекладываем
        return;
      }
      // динамика
      S.length = 0;
      initDynamic();
      last = performance.now();
      raf = requestAnimationFrame(tick);
    };

    const stop = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    };

    // реагируем на MQ изменения
    const onMQ = (ev) => {
      disabled = ev.matches;
      stop();
      start();
    };
    if (mq.addEventListener) mq.addEventListener('change', onMQ);
    else mq.addListener(onMQ); // Safari старый

    // реагируем на resize
    const onResize = () => {
      if (disabled) {
        placeStatic();
      } else {
        // корректируем в пределах коробки без дёрготни
        const { w: W, h: H } = box();
        for (const s of S) {
          s.x = Math.max(0, Math.min(W - s.w, s.x));
          s.y = Math.max(0, Math.min(H - s.h, s.y));
          s.el.style.transform =
              `translate3d(${s.x.toFixed(1)}px, ${s.y.toFixed(1)}px, 0)`;
        }
      }
    };
    window.addEventListener('resize', onResize);

    // пауза по видимости секции (только для динамики)
    const io = new IntersectionObserver((entries) => {
      if (disabled) return;
      const e = entries[0];
      if (e?.isIntersecting) {
        if (!raf) { last = performance.now(); raf = requestAnimationFrame(tick); }
      } else {
        stop();
      }
    }, { root: null, threshold: 0.01 });
    io.observe(container);

    // init
    start();

    return () => {
      stop();
      io.disconnect();
      window.removeEventListener('resize', onResize);
      if (mq.removeEventListener) mq.removeEventListener('change', onMQ);
      else mq.removeListener(onMQ);
    };
  }, []);




  return (
      <div className="min-h-screen ">

        <div className="w-full h-screen relative" ref={heroRef}>
          {/* Pineapple center */}

          {floatingItems.map((f, i) => (
              <div
                  key={i}
                  className="fruit"
                  data-start-top={f.style?.["--start-top"] || "50%"}
                  data-start-left={f.style?.["--start-left"] || "50%"}
                  data-tx={f.style?.["--tx"] || "0"}
                  data-ty={f.style?.["--ty"] || "0"}
                  data-size={f.size || ""}                 
                  data-pulse-amp={f.pulse?.amp?.join(",") || ""}    
                  data-pulse-period={f.pulse?.period?.join(",") || ""} 
                  data-z-weight={typeof f.zWeight === "number" ? f.zWeight : ""} 
                  style={{ width: f.size || "12vmin", height: "auto" }}
                  ref={(el) => {
                    if (!el) return;
                    if (!el._img) {
                      const img = new Image();
                      img.src = f.src;
                      img.alt = f.alt;
                      el.appendChild(img);
                      el._img = img;
                    }
                  }}
              />
          ))}



          {/* Center logo */}
          <div className="w-full h-full flex justify-center items-center relative mt-[-6rem] hero-logo">
            <img
                src={logo}
                alt="Main Logo"
                className="main-logo object-contain mx-auto"
            />
          </div>
        </div>
        <div className="pineapple" ref={pineappleRef}>
          <img src={ananas} ref={pineappleImgRef} alt="Ananas" className="fruit-center"/>
        </div>
        {/* Second Section */}
        <div className="relative min-h-screen pt-32 sm:pt-64 overflow-x-hidden">
          <div className="mx-auto  pt-10">
            <div className="text-container" ref={textRef}>
  <h3 className={`code-text ${showText}`}>
   CODE -  революция в мире электронных сигарет
  </h3>
  <p className={`code-subtext ${showText}`}>
    Мы провели большое исследование, опросили тысячи потребителей и выяснили, каким должен быть тот самый идеальный вейп. Сегодня мы представляем вам то, о чем вы давно мечтали - идеальный вейп CODE!
  </p>
</div>


            {/* Smoke background + features */}
            <div className="relative w-full min-h-screen flex justify-center items-center overflow-hidden">
              {/* Background smoke */}
              <img
                  src={smoke3}
                  alt="smoke"
                  className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              />
              <img
                  src={smoke}
                  alt="smoke"
                  className="absolute inset-0 object-cover opacity-50 pointer-events-none left-1/2"
              />
              <img
                  src={smoke}
                  alt="smoke"
                  className="absolute inset-0 object-cover opacity-50 pointer-events-none"
              />

              {/* Features row */}
              <div
                  className="relative z-10 flex  justify-center items-center gap-8 sm:gap-10 md:gap-14 max-w-screen-2xl mx-auto px-6">
                {features.map((feature, index) => (
                    <div
                        key={feature.id}
                        style={{animationDelay: `${index * 150}ms`}}
                        className={`text-center ${
                            index === 1
                                ? "z-10 scale-110 animate-float"
                                : "z-0 animate-float"
                        }`}
                    >
                      <div className="flex justify-center">
                        <img
                            src={feature.img}
                            alt={feature.title}
                            className={`object-contain transition-transform duration-300
              ${
                                index === 1
                                    ? "w-[10rem] sm:w-[14rem] md:w-[18rem] lg:w-[22rem]"
                                    : "w-[6rem] sm:w-[10rem] md:w-[14rem] lg:w-[18rem]"
                            }`}
                        />
                      </div>
                    </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
  );
});

export default HeroSection;
